import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CustomButton from '../../../components/@extended/CustomButton';
import { useTheme } from '@emotion/react';
import { useUpdateUserDetailsMutation, useSystemAdminResetPasswordMutation } from '../../../store/slices/apiSlice';
import { useDispatch } from 'react-redux';
import { openMessage } from '../../../store/slices/showMessageSlice';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import moment from 'moment';

// LabelContainer component for editable fields
const LabelContainer = ({ 
  label, 
  value, 
  valueFontWeight, 
  valueColor, 
  fieldName, 
  onEdit, 
  isEditing, 
  onSave, 
  onCancel, 
  onValueChange,
  editable = true 
}) => {
  const [hovered, setHovered] = useState(false);
  const [editValue, setEditValue] = useState(value);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    onSave(fieldName, editValue);
  };

  const handleCancel = () => {
    setEditValue(value);
    onCancel(fieldName);
  };

  const handleValueChange = (event) => {
    setEditValue(event.target.value);
    if (onValueChange) {
      onValueChange(fieldName, event.target.value);
    }
  };

  return (
    <Grid container py={1}>
      <Grid item xs={4}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            '&:hover .edit-icon': {
              opacity: editable ? 1 : 0,
            },
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {isEditing ? (
            <TextField
              value={editValue}
              onChange={handleValueChange}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSave} color="primary">
                      <SaveIcon />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancel} color="error">
                      <CancelIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <>
              <Typography variant="body1" fontWeight={valueFontWeight} color={valueColor}>
                {value}
              </Typography>
              {editable && (
                <IconButton
                  size="small"
                  className="edit-icon"
                  onClick={() => onEdit(fieldName)}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    ml: 1,
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

// Password generation utility
const generateRandomPassword = () => {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  
  // Ensure at least one of each required character
  let password = '';
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];
  
  // Fill the rest randomly
  const allChars = uppercase + lowercase + numbers + special;
  for (let i = password.length; i < 8; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

const passwordValidationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('New password is required')
    .matches(/^\S*$/, 'Whitespace is not allowed')
    .min(8, 'Password must be at least 8 characters long')
    .max(20, 'Password must be at most 20 characters long')
    .matches(/(?=.*[0-9])/, 'Password requires at least one number')
    .matches(/(?=.*[A-Z])/, 'Password requires at least one uppercase letter')
    .matches(/(?=.*[a-z])/, 'Password requires at least one lowercase letter')
    .matches(/(?=.*\W)/, 'Password requires at least one special character'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
});

const BasicInfoTab = ({ user, sponsorId, onUserUpdate }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [updateUserDetails, { isLoading: isUpdating }] = useUpdateUserDetailsMutation();
  const [systemAdminResetPassword, { isLoading: isResetting }] = useSystemAdminResetPasswordMutation();
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({});

  // Calculate password expiry and time remaining
  const calculatePasswordExpiry = () => {
    if (!user?.lastResetAt) return null;
    
    const lastResetAt = typeof user.lastResetAt === 'string' 
      ? new Date(user.lastResetAt) 
      : user.lastResetAt;
    
    if (!lastResetAt) return null;
    
    const expiryDate = new Date(lastResetAt);
    if (user.tempPassword) {
      expiryDate.setDate(expiryDate.getDate() + 1); // Temp password expires in 1 day
    } else {
      expiryDate.setDate(expiryDate.getDate() + 90); // Normal password expires in 90 days
    }
    
    return expiryDate;
  };

  const calculateTimeRemaining = () => {
    const expiryDate = calculatePasswordExpiry();
    if (!expiryDate) return null;
    
    const now = new Date();
    const diff = expiryDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days} days, ${hours} hours`;
    if (hours > 0) return `${hours} hours, ${minutes} minutes`;
    return `${minutes} minutes`;
  };

  const passwordExpiry = calculatePasswordExpiry();
  const timeRemaining = calculateTimeRemaining();

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: passwordValidationSchema,
    onSubmit: async (values) => {
      try {
        await systemAdminResetPassword({
          userId: user.id,
          newPassword: values.newPassword,
        }).unwrap();
        
        dispatch(openMessage({ 
          messageSeverity: 'success', 
          message: 'Password reset successfully' 
        }));
        
        setShowResetPasswordForm(false);
        formik.resetForm();
        if (onUserUpdate) onUserUpdate();
      } catch (error) {
        dispatch(openMessage({ 
          messageSeverity: 'error', 
          message: error?.data?.message || 'Failed to reset password' 
        }));
      }
    },
  });

  const handleGeneratePassword = () => {
    const generatedPassword = generateRandomPassword();
    formik.setFieldValue('newPassword', generatedPassword);
    formik.setFieldValue('confirmPassword', generatedPassword);
  };

  const handleToggleTempPassword = async (enabled) => {
    try {
      await updateUserDetails({
        sponsorId,
        userId: user.id,
        body: { tempPassword: enabled }
      }).unwrap();
      dispatch(openMessage({ 
        messageSeverity: 'success', 
        message: `Temporary password ${enabled ? 'enabled' : 'disabled'} successfully` 
      }));
      if (onUserUpdate) onUserUpdate();
    } catch (error) {
      dispatch(openMessage({ messageSeverity: 'error', message: 'Failed to update password setting' }));
    }
  };

  const handleToggleEmailVerified = async (verified) => {
    try {
      await updateUserDetails({
        sponsorId,
        userId: user.id,
        body: { isEmailVerified: verified }
      }).unwrap();
      dispatch(openMessage({ 
        messageSeverity: 'success', 
        message: `Email verification ${verified ? 'enabled' : 'disabled'} successfully` 
      }));
      if (onUserUpdate) onUserUpdate();
    } catch (error) {
      dispatch(openMessage({ messageSeverity: 'error', message: 'Failed to update email verification' }));
    }
  };

  const handleTogglePhoneVerified = async (verified) => {
    try {
      await updateUserDetails({
        sponsorId,
        userId: user.id,
        body: { isPhoneVerified: verified }
      }).unwrap();
      dispatch(openMessage({ 
        messageSeverity: 'success', 
        message: `Phone verification ${verified ? 'enabled' : 'disabled'} successfully` 
      }));
      if (onUserUpdate) onUserUpdate();
    } catch (error) {
      dispatch(openMessage({ messageSeverity: 'error', message: 'Failed to update phone verification' }));
    }
  };

  const handleEdit = (fieldName) => {
    setEditingField(fieldName);
    setFormData(prev => ({
      ...prev,
      [fieldName]: user?.[fieldName] || ''
    }));
  };

  const handleSave = async (fieldName, value) => {
    try {
      const updateData = {
        [fieldName]: value.trim ? value.trim() : value
      };
      
      await updateUserDetails({
        sponsorId,
        userId: user.id,
        body: updateData
      }).unwrap();
      
      setEditingField(null);
      setFormData(prev => {
        const newData = { ...prev };
        delete newData[fieldName];
        return newData;
      });
      
      dispatch(openMessage({ 
        messageSeverity: 'success', 
        message: 'User details updated successfully' 
      }));
      
      if (onUserUpdate) onUserUpdate();
    } catch (error) {
      dispatch(openMessage({ 
        messageSeverity: 'error', 
        message: error?.data?.message || 'Failed to update user details' 
      }));
    }
  };

  const handleCancel = (fieldName) => {
    setEditingField(null);
    setFormData(prev => {
      const newData = { ...prev };
      delete newData[fieldName];
      return newData;
    });
  };

  const handleValueChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  return (
    <Box sx={{ p: 3, pt: 0 }}>
      {/* User Basic Details */}
      <Typography variant="h6" gutterBottom fontWeight={600}>
        User Details
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <LabelContainer 
            label={'First Name'} 
            value={user?.firstName || 'N/A'} 
            fieldName="firstName"
            isEditing={editingField === 'firstName'}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            onValueChange={handleValueChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LabelContainer 
            label={'Last Name'} 
            value={user?.lastName || 'N/A'} 
            fieldName="lastName"
            isEditing={editingField === 'lastName'}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            onValueChange={handleValueChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LabelContainer 
            label={'Email'} 
            value={user?.email || 'N/A'} 
            fieldName="email"
            isEditing={editingField === 'email'}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            onValueChange={handleValueChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LabelContainer 
            label={'Contact Number'} 
            value={user?.contactNumber || 'N/A'} 
            fieldName="contactNumber"
            isEditing={editingField === 'contactNumber'}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            onValueChange={handleValueChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LabelContainer 
            label={'Address'} 
            value={user?.address || 'N/A'} 
            fieldName="address"
            isEditing={editingField === 'address'}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            onValueChange={handleValueChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LabelContainer
            label={'Created Date'}
            value={user?.createdAt ? moment(user.createdAt).format('DD/MM/YYYY HH:mm') : 'N/A'}
            editable={false}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Metadata Section */}
      <Typography variant="h6" gutterBottom fontWeight={600}>
        Metadata
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={user?.tempPassword || false}
                onChange={(e) => handleToggleTempPassword(e.target.checked)}
                disabled={isUpdating}
              />
            }
            label="Temporary Password Enabled"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={user?.isEmailVerified || false}
                onChange={(e) => handleToggleEmailVerified(e.target.checked)}
                disabled={isUpdating}
              />
            }
            label="Email Verified"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={user?.isPhoneVerified || false}
                onChange={(e) => handleTogglePhoneVerified(e.target.checked)}
                disabled={isUpdating}
              />
            }
            label="Phone Verified"
          />
        </Grid>
        {timeRemaining && (
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Time Remaining for Password Reset</Typography>
            <Typography 
              variant="body1" 
              color={timeRemaining === 'Expired' ? 'error.main' : 'text.primary'}
            >
              {timeRemaining}
            </Typography>
          </Grid>
        )}
        {passwordExpiry && (
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Password Expiry</Typography>
            <Typography variant="body1">
              {moment(passwordExpiry).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Password Reset Section */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Reset Password
          </Typography>
          {!showResetPasswordForm && (
            <CustomButton
              variant="contained"
              onClick={() => setShowResetPasswordForm(true)}
            >
              Reset Password
            </CustomButton>
          )}
        </Box>

        {showResetPasswordForm && (
          <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                  helperText={formik.touched.newPassword && formik.errors.newPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <Tooltip title="Generate Random Password">
                          <IconButton onClick={handleGeneratePassword} edge="end">
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                  helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <CustomButton
                    type="submit"
                    variant="contained"
                    disabled={isResetting}
                    startIcon={isResetting && <CircularProgress size={16} />}
                  >
                    {isResetting ? 'Resetting...' : 'Set New Password'}
                  </CustomButton>
                  <CustomButton
                    variant="outlined"
                    onClick={() => {
                      setShowResetPasswordForm(false);
                      formik.resetForm();
                    }}
                    disabled={isResetting}
                  >
                    Cancel
                  </CustomButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BasicInfoTab;

